'use client';

import type { PostArticleProps } from '@routeroyal/theme-core';
import { MarkdownRenderer, useMarkdownHeadings } from '@routeroyal/ui-core';
import Image from 'next/image';
import Link from 'next/link';
import { useState, useEffect } from 'react';

/**
 * Route Royal Post Article
 * 
 * Full article display with professional styling
 */
export function RouteRoyalPostArticle({
  post,
  relatedPosts = [],
  showShareButtons = true,
  showAuthorBio = true,
  showRelatedPosts = true,
  showTableOfContents = false,
  onShare,
  locale = 'en',
  basePath = '/blog',
  className = '',
  messages,
}: PostArticleProps) {
  const [activeHeadingId, setActiveHeadingId] = useState<string>();
  const [copiedLink, setCopiedLink] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Extract headings from content for TOC using the core hook
  const allHeadings = useMarkdownHeadings(post.content, post.contentFormat);
  const headings = showTableOfContents ? allHeadings : [];

  useEffect(() => {
    if (!showTableOfContents || headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings, showTableOfContents]);

  const handleShare = (platform: 'twitter' | 'facebook' | 'linkedin' | 'copy') => {
    if (onShare) {
      onShare(platform);
      return;
    }

    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = post.title;

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
        break;
    }
  };

  return (
    <article className={`max-w-4xl mx-auto ${className}`}>
      {/* Header */}
      <header className="mb-8">
        {post.category && (
          <div className="mb-4">
            <span className="inline-block px-3 py-1 text-sm font-semibold text-blue-700 bg-blue-100 rounded-lg">
              {post.category.name}
            </span>
          </div>
        )}

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="text-xl text-gray-600 mb-6 leading-relaxed">
            {post.excerpt}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-4 text-gray-600 border-t border-b border-gray-200 py-4">
          <div className="flex items-center gap-3">
            {post.author.avatar || post.author.avatarUrl ? (
              <div className="relative w-10 h-10 rounded-full overflow-hidden">
                <Image
                  src={post.author.avatar || post.author.avatarUrl || ''}
                  alt={post.author.name}
                  fill
                  className="object-cover object-top"
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold">
                  {post.author.name.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <div className="font-semibold text-gray-900">{post.author.name}</div>
              {(post.author.role || post.author.authorTitle) && (
                <div className="text-sm text-gray-500">{post.author.role || post.author.authorTitle}</div>
              )}
            </div>
          </div>

          <span className="hidden sm:inline text-gray-300">|</span>

          {post.publishedAt && (
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
            </div>
          )}

          {(post.readingTime || post.readingTimeMinutes) && (
            <>
              <span className="hidden sm:inline text-gray-300">|</span>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{post.readingTime || post.readingTimeMinutes} {messages?.minuteRead || 'min read'}</span>
              </div>
            </>
          )}

          {showShareButtons && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500 hidden sm:inline">{messages?.share || 'Share'}:</span>
              {['twitter', 'linkedin', 'copy'].map((platform) => (
                <button
                  key={platform}
                  onClick={() => handleShare(platform as 'twitter' | 'linkedin' | 'copy')}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label={`Share on ${platform}`}
                >
                  {platform === 'twitter' && (
                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  )}
                  {platform === 'linkedin' && (
                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  )}
                  {platform === 'copy' && (
                    copiedLink ? (
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                    )
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="relative w-full aspect-[16/9] md:aspect-[2/1] mb-10 rounded-xl overflow-hidden">
          <Image
            src={post.featuredImage.url}
            alt={post.featuredImage.alt || post.title}
            fill
            className="object-cover"
            priority
          />
          {post.featuredImage.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-sm p-3">
              {post.featuredImage.caption}
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div className={`relative ${showTableOfContents && headings.length > 0 ? 'lg:grid lg:grid-cols-[1fr_250px] lg:gap-8' : ''}`}>
        {/* Table of Contents */}
        {showTableOfContents && headings.length > 0 && (
          <aside className="hidden lg:block lg:order-2">
            <div className="sticky top-24">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                Contents
              </h4>
              <ul className="space-y-2 text-sm">
                {headings.map((heading) => (
                  <li
                    key={heading.id}
                    style={{ paddingLeft: `${(heading.level - 2) * 0.75}rem` }}
                  >
                    <button
                      onClick={() => {
                        const element = document.getElementById(heading.id);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className={`
                        text-left w-full py-1 border-l-2 pl-3 -ml-px transition-all duration-200
                        ${activeHeadingId === heading.id
                          ? 'border-blue-600 text-blue-600 font-medium'
                          : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
                        }
                      `}
                    >
                      {heading.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}

        {/* Article Content */}
        <div className="lg:order-1">
          <MarkdownRenderer
            content={post.content}
            contentFormat={post.contentFormat}
            theme="route-royal"
          />
        </div>
      </div>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-200">
          {post.tags.map((tag) => (
            <span
              key={tag.id}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer"
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      {/* Author Bio */}
      {showAuthorBio && (
        <div className="mt-10 pt-8 border-t border-gray-200">
          <div className="flex gap-5 p-6 bg-gray-50 rounded-xl">
            {post.author.avatar || post.author.avatarUrl ? (
              <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={post.author.avatar || post.author.avatarUrl || ''}
                  alt={post.author.name}
                  fill
                  className="object-cover object-top"
                />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-bold text-xl">
                  {post.author.name.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <h3 className="text-lg font-bold text-gray-900">{post.author.name}</h3>
              {(post.author.role || post.author.authorTitle) && (
                <p className="text-sm text-gray-500 mb-2">{post.author.role || post.author.authorTitle}</p>
              )}
              {(post.author.bio || post.author.authorBio) && (
                <p className="text-gray-600">{post.author.bio || post.author.authorBio}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Related Posts */}
      {showRelatedPosts && relatedPosts.length > 0 && (
        <section className="mt-16 pt-10 border-t border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            {messages?.relatedPosts || 'Related Posts'}
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {relatedPosts.map((relatedPost) => (
              <Link
                key={relatedPost.id}
                href={`${basePath}/${relatedPost.slug}`}
                className="group block bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
              >
                {relatedPost.featuredImage && (
                  <div className="relative aspect-video overflow-hidden">
                    <Image
                      src={relatedPost.featuredImage.url}
                      alt={relatedPost.title}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {relatedPost.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
